import Alpine, {DirectiveData, Magics} from 'alpinejs';
import {isSavingData, isSlowConnection} from '@theme/utils';

// Track URLs that have been preloaded or are in progress (global to prevent duplicates across instances)
const preloadedUrls = new Set<string>();

/** Event types used for interact-based preload (delegated on root). */
const PRELOAD_INTERACT_EVENTS = ['click', 'touchstart', 'focusin', 'focus', 'pointerover'] as const;

Alpine.directive('preloader', ($el, {expression, modifiers}: DirectiveData) => {
  let preloadOnIntersect = modifiers.includes('intersect');
  let preloadOnInteract = modifiers.includes('interact');

  if (!modifiers.includes('intersect') && !modifiers.includes('interact')) {
    preloadOnIntersect = true;
    preloadOnInteract = true;
  }

  Alpine.bind($el, {
    'x-data'(this: Magics<any> & {__preloader_sectionId: string}) {
      // Single AbortController for all batch requests
      const sectionId = expression || this.$sectionId!;

      return {
        __preloader_batchAbortController: new AbortController(),

        /** AbortController for interact delegation listeners (aborting removes all listeners). */
        __preloader_interactAbortController: undefined as AbortController | undefined,

        __preloader_sectionId: sectionId,
        __preloader_getUrls(): string[] {
          // Collect all URLs from children with data-preload-url attribute
          const urls: string[] = [];
          $el.querySelectorAll('[data-preload-url]').forEach((child) => {
            const url = (child as HTMLElement).dataset.preloadUrl;
            if (url) {
              urls.push(url);
            }
          });
          return urls;
        },

        __preloader_cancelBatchRequests(this: any) {
          // Abort all pending batch requests
          this.__preloader_batchAbortController.abort();
        },

        /**
         * Preload a single URL (used when user interacts with an element that has data-preload-url).
         * Uses high priority so the target is ready before navigation.
         */
        __preloader_preloadOne(this: any, url: string) {
          if (isSavingData() || isSlowConnection()) return;
          if (!url || preloadedUrls.has(url)) return;
          preloadedUrls.add(url);
          if (!this.$sectionApi?.load) return;
          this.$sectionApi
            ?.load({
              sectionId: this.__preloader_sectionId,
              baseUrl: url,
              abortPending: false,
              waitForIdle: false,
              priority: 'high',
            } as any)
            ?.catch(() => {
              // Ignore load errors (e.g. navigation aborted)
            });
        },

        /**
         * Delegated handler: when interaction happens on a descendant with data-preload-url, preload that URL.
         */
        __preloader_handlePreloadInteract(this: any, event: Event) {
          const root = this.$el as HTMLElement;
          const target =
            (event.target as Node)?.nodeType === Node.ELEMENT_NODE
              ? (event.target as HTMLElement).closest?.('[data-preload-url]')
              : null;
          if (!target || !root.contains(target)) return;
          const url = (target as HTMLElement).dataset?.preloadUrl;
          if (url) this.__preloader_preloadOne(url);
        },

        __preloader_preloadAll(this: any) {
          const sectionsStore = this.$store?.sections as any;
          if (isSavingData() || isSlowConnection()) return;
          if (!sectionsStore?.intersected?.has(this.$sectionId)) return;

          const urls = this.__preloader_getUrls();
          const newUrls: string[] = [];

          if (!this.$sectionApi?.load) return;

          // Filter out URLs that have already been preloaded
          urls.forEach((url: string) => {
            if (!preloadedUrls.has(url)) {
              newUrls.push(url);
              preloadedUrls.add(url);
            }
          });

          const sectionId = this.__preloader_sectionId;

          // Preload with low priority (batch requests)
          newUrls.forEach((url) => {
            this.$sectionApi
              ?.load({
                sectionId,
                baseUrl: url,
                abortPending: false,
                waitForIdle: true,
                priority: 'low',
                signal: this.__preloader_batchAbortController.signal,
              } as any)
              ?.catch(() => {
                // Ignore abort errors
              });
          });
        },

        init(this: any) {
          if (!preloadOnInteract) return;
          const controller = new AbortController();
          this.__preloader_interactAbortController = controller;
          const root = this.$el as HTMLElement;
          const {signal} = controller;
          PRELOAD_INTERACT_EVENTS.forEach((type) => {
            root.addEventListener(type, this.__preloader_handlePreloadInteract.bind(this), {
              passive: type === 'touchstart',
              signal,
            });
          });
        },

        destroy(this: any) {
          // Cancel all pending batch requests when component is destroyed
          this.__preloader_cancelBatchRequests();
          // Abort interact delegation (removes all listeners registered with the signal)
          this.__preloader_interactAbortController?.abort();
        },
      };
    },

    'x-effect'(this: Magics<any> & {__preloader_preloadAll(): void}) {
      if (preloadOnIntersect) {
        if (this.$store?.sections?.intersected?.has(this.$sectionId!)) {
          this.__preloader_preloadAll();
        }
      }
    },
  });
});
